import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Settings as SettingsIcon, DollarSign, Building, Save, Check } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';

const Settings = () => {
  const { settings, updateSettings, CURRENCY_CONFIG, formatCurrency } = useSettings();
  const { canManageSettings, isAdmin } = useAuth();
  const [formData, setFormData] = useState({
    currency: settings.currency,
    company_name: settings.company_name
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(formData);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const previewAmount = 1234.56;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Settings</h1>
          <p className="text-slate-500 mt-1">Configure system preferences</p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Currency Settings */}
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Currency</CardTitle>
                <CardDescription>Set the currency for all monetary values</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => setFormData({ ...formData, currency: value })}
                disabled={!canManageSettings()}
              >
                <SelectTrigger className="rounded-sm" data-testid="currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CURRENCY_CONFIG).map(([code, config]) => (
                    <SelectItem key={code} value={code}>
                      <span className="flex items-center gap-2">
                        <span className="font-mono">{config.symbol}</span>
                        <span>{config.name} ({code})</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Preview */}
            <div className="bg-slate-50 rounded-sm p-4">
              <p className="text-sm text-slate-500 mb-2">Preview</p>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Sample amount:</span>
                <span className="font-mono font-bold text-lg">
                  {CURRENCY_CONFIG[formData.currency]?.symbol} {previewAmount.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Currency Info */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">Available Currencies</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(CURRENCY_CONFIG).map(([code, config]) => (
                  <Badge
                    key={code}
                    variant={formData.currency === code ? "default" : "secondary"}
                    className="rounded-sm"
                  >
                    {config.symbol} {code}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Settings */}
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                <Building className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Company</CardTitle>
                <CardDescription>Company information settings</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                disabled={!canManageSettings()}
                className="rounded-sm"
                data-testid="company-name-input"
              />
            </div>

            {/* Current Settings */}
            <div className="bg-slate-50 rounded-sm p-4 space-y-2">
              <p className="text-sm text-slate-500">Current Settings</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-slate-600">Currency:</span>
                <span className="font-semibold">{settings.currency}</span>
                <span className="text-slate-600">Company:</span>
                <span className="font-semibold">{settings.company_name}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      {isAdmin() && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <span className="animate-spin mr-2">⏳</span>
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>
      )}

      {!isAdmin() && (
        <Card className="border-amber-200 bg-amber-50 rounded-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <SettingsIcon className="w-5 h-5 text-amber-600" />
            <p className="text-sm text-amber-800">
              Only administrators can modify settings. Contact your admin to make changes.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Settings;
